<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import adsConnectionsAPI from 'dashboard/api/adsConnections';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const props = defineProps({
  provider: { type: String, required: true },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const providerKey = computed(() => props.provider.toUpperCase());
const integrationId = computed(() => `${props.provider}_ads`);
const i18n = (key, params) => t(`INTEGRATION_SETTINGS.ADS.${key}`, params);

const connection = ref(null);
const options = ref([]);
const emptyReason = ref('');
const errorInfo = ref(null);
const selectedOption = ref('');
const isLoading = ref(true);
const isSaving = ref(false);
const isSelecting = ref(false);
const disconnectDialog = ref(null);

const status = computed(() => connection.value?.status);
const needsSelection = computed(
  () => status.value === 'pending' || isSelecting.value
);
const selectOptions = computed(() =>
  options.value.map(option => ({
    value: option.id,
    label: `${option.external_account_name} — ${option.resource_name}`,
  }))
);

const TOAST_DURATION = 8000;

const PROVIDER_MESSAGES = [
  [/only approved for use with test accounts/i, 'ERRORS.TEST_ACCOUNTS_ONLY'],
  [/api version not found/i, 'ERRORS.API_VERSION'],
  [/developer.token/i, 'ERRORS.DEVELOPER_TOKEN'],
  [/permission|not authorized|access denied/i, 'ERRORS.PROVIDER_PERMISSION'],
];

const showError = (message, detail = '') => {
  errorInfo.value = { message, detail };
  useAlert(message, { duration: TOAST_DURATION });
};

const translateProviderMessage = raw => {
  const match = PROVIDER_MESSAGES.find(([pattern]) => pattern.test(raw));
  return i18n(match ? match[1] : 'ERRORS.PROVIDER_FAILED');
};

const handleError = error => {
  const data = error?.response?.data;
  if (data?.error === 'reauthorization_required') {
    connection.value = { ...connection.value, status: data.error };
    isSelecting.value = false;
    showError(i18n('ERRORS.REAUTHORIZATION_REQUIRED'));
  } else if (data?.error === 'provider_error') {
    showError(translateProviderMessage(data.message), data.message);
  } else if (data?.error === 'not_configured') {
    showError(i18n('ERRORS.NOT_CONFIGURED'));
  } else {
    showError(i18n('ERRORS.GENERIC'));
  }
};

const loadOptions = async () => {
  errorInfo.value = null;
  try {
    const { data } = await adsConnectionsAPI.listOptions(props.provider);
    options.value = data.options;
    emptyReason.value = data.reason || '';
    selectedOption.value = '';
  } catch (error) {
    handleError(error);
  }
};

const loadConnection = async () => {
  const { data } = await adsConnectionsAPI.show(props.provider);
  connection.value = data;
  if (data.status === 'pending') await loadOptions();
};

const authorize = async () => {
  errorInfo.value = null;
  try {
    const { data } = await adsConnectionsAPI.authorize(props.provider);
    window.location.href = data.url;
  } catch (error) {
    handleError(error);
  }
};

const startSelection = async () => {
  isSelecting.value = true;
  await loadOptions();
};

const saveSelection = async () => {
  isSaving.value = true;
  try {
    const { data } = await adsConnectionsAPI.select(
      props.provider,
      selectedOption.value
    );
    connection.value = data;
    isSelecting.value = false;
    errorInfo.value = null;
    useAlert(i18n('SAVED'));
  } catch (error) {
    handleError(error);
  } finally {
    isSaving.value = false;
  }
};

const disconnect = async () => {
  disconnectDialog.value.close();
  try {
    await adsConnectionsAPI.disconnect(props.provider);
    connection.value = { ...connection.value, status: 'not_connected' };
    isSelecting.value = false;
    useAlert(i18n('DISCONNECTED'));
  } catch (error) {
    handleError(error);
  }
};

onMounted(async () => {
  const { error } = route.query;
  if (error) {
    const reasons = {
      access_denied: 'ERRORS.ACCESS_DENIED',
      missing_permissions: 'ERRORS.MISSING_PERMISSIONS',
    };
    showError(i18n(reasons[error] || 'ERRORS.FAILED'), error);
    router.replace({ query: {} });
  }
  try {
    await loadConnection();
  } catch (e) {
    handleError(e);
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="i18n(`${providerKey}.HEADER`)"
        description=""
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div
        v-if="connection"
        class="flex flex-col gap-6 p-6 outline outline-n-container outline-1 bg-n-card rounded-xl"
      >
        <div class="flex items-center gap-6">
          <div class="flex h-16 w-16 items-center justify-center flex-shrink-0">
            <img
              :src="`/dashboard/images/integrations/${integrationId}.png`"
              class="max-w-full rounded-md border border-n-weak shadow-sm block dark:hidden bg-n-alpha-3"
            />
            <img
              :src="`/dashboard/images/integrations/${integrationId}-dark.png`"
              class="max-w-full rounded-md border border-n-weak shadow-sm hidden dark:block bg-n-alpha-2"
            />
          </div>
          <div class="flex-1">
            <h3 class="mb-1 text-heading-1 text-n-slate-12">
              {{ i18n(`${providerKey}.HEADER`) }}
            </h3>
            <p class="text-n-slate-11 text-body-main">
              {{ i18n(`${providerKey}.DESCRIPTION`) }}
            </p>
          </div>
        </div>

        <details
          v-if="errorInfo"
          class="rounded-lg border border-n-ruby-6 bg-n-ruby-3 p-3"
        >
          <summary
            class="cursor-pointer text-body-main font-medium text-n-ruby-11"
          >
            {{ i18n('ERRORS.TITLE') }}
          </summary>
          <p class="mt-2 mb-0 text-body-main text-n-ruby-11">
            {{ errorInfo.message }}
          </p>
          <p
            v-if="errorInfo.detail"
            class="mt-2 mb-0 text-body-main text-n-slate-11"
          >
            {{ i18n('ERRORS.DETAILS_LABEL') }}
            <code class="break-words">{{ errorInfo.detail }}</code>
          </p>
        </details>

        <p
          v-if="!connection.configured"
          class="text-body-main text-n-ruby-11 m-0"
        >
          {{ i18n('ERRORS.NOT_CONFIGURED') }}
        </p>

        <div v-else-if="needsSelection" class="flex flex-col gap-3 max-w-xl">
          <p class="text-body-main text-n-slate-12 m-0">
            {{ i18n(`${providerKey}.SELECT_TITLE`) }}
          </p>
          <p
            v-if="!selectOptions.length"
            class="text-body-main text-n-slate-11 m-0"
          >
            {{
              i18n(
                emptyReason === 'no_accounts'
                  ? `${providerKey}.NO_ACCOUNTS`
                  : `${providerKey}.NO_OPTIONS`
              )
            }}
          </p>
          <Select
            v-else
            v-model="selectedOption"
            :options="selectOptions"
            :placeholder="i18n('SELECT_PLACEHOLDER')"
          />
          <div class="flex gap-2">
            <Button
              :label="i18n('SAVE')"
              :is-loading="isSaving"
              :disabled="!selectedOption"
              @click="saveSelection"
            />
            <Button
              v-if="isSelecting"
              faded
              slate
              :label="i18n('CANCEL')"
              @click="isSelecting = false"
            />
            <Button
              v-else
              faded
              ruby
              :label="i18n('DISCONNECT')"
              @click="disconnectDialog.open()"
            />
          </div>
        </div>

        <div
          v-else-if="status === 'connected'"
          class="flex flex-col gap-3 max-w-xl"
        >
          <p class="text-body-main text-n-slate-12 m-0">
            {{
              i18n('CONNECTED_TO', {
                account: connection.external_account_name,
                resource: connection.resource_name,
              })
            }}
          </p>
          <div class="flex gap-2">
            <Button
              faded
              blue
              :label="i18n(`${providerKey}.CHANGE`)"
              @click="startSelection"
            />
            <Button
              faded
              ruby
              :label="i18n('DISCONNECT')"
              @click="disconnectDialog.open()"
            />
          </div>
        </div>

        <div v-else class="flex flex-col gap-3 max-w-xl">
          <p
            v-if="!errorInfo && status === 'reauthorization_required'"
            class="text-body-main text-n-ruby-11 m-0"
          >
            {{ i18n('ERRORS.REAUTHORIZATION_REQUIRED') }}
          </p>
          <div>
            <Button
              faded
              blue
              :label="
                status === 'reauthorization_required'
                  ? i18n('RECONNECT')
                  : $t('INTEGRATION_SETTINGS.CONNECT.BUTTON_TEXT')
              "
              @click="authorize"
            />
          </div>
        </div>

        <p class="text-body-main text-n-slate-11 m-0">
          {{ i18n('LGPD_NOTICE') }}
        </p>
      </div>

      <Dialog
        ref="disconnectDialog"
        type="alert"
        :title="i18n('DELETE.TITLE')"
        :description="i18n('DELETE.MESSAGE')"
        :confirm-button-label="i18n('DELETE.CONFIRM')"
        :cancel-button-label="i18n('DELETE.CANCEL')"
        @confirm="disconnect"
      />
    </template>
  </SettingsLayout>
</template>
